<template>
  <!-- Doppelpille „Unternehmensvorlagen“ / „Eigene Vorlagen“
       (Kundenfeedback Kanzlei Polz 25.10.2026 Teil 2, II und IV): weiß,
       ausgewählt blau; Erklärung als Tooltip. Pfeiltasten wechseln wie bei
       einer Optionsgruppe. -->
  <div
    :aria-label="$gt('Welche Vorlagen?')"
    class="tos"
    data-testid="template-owner-switch"
    role="radiogroup"
    @keydown="onKeydown"
  >
    <ChatTip
      v-for="option in TEMPLATE_OWNER_OPTIONS"
      :key="option.value"
      location="bottom"
      :text="$gt(option.hint)"
      :title="$gt(option.label)"
    >
      <button
        :ref="el => setButtonRef(option.value, el)"
        :aria-checked="modelValue === option.value ? 'true' : 'false'"
        class="tos__btn"
        :class="{ 'is-active': modelValue === option.value }"
        :data-testid="`template-owner-${option.value}`"
        role="radio"
        :tabindex="modelValue === option.value ? 0 : -1"
        type="button"
        @click="choose(option.value)"
      >
        <component :is="option.value === 'own' ? UserRound : Building2" aria-hidden="true" :size="14" />
        {{ $gt(option.label) }}
        <span v-if="counts" class="tos__count">{{ counts[option.value] }}</span>
      </button>
    </ChatTip>
  </div>
</template>

<script setup lang="ts">
  import type { ComponentPublicInstance } from 'vue'
  import { Building2, UserRound } from 'lucide-vue-next'
  import ChatTip from '@/components/messages/chat/ChatTip.vue'
  import { TEMPLATE_OWNER_OPTIONS, type TemplateOwner } from '@/utils/mailTemplateFilter'

  const props = defineProps<{
    modelValue: TemplateOwner
    /** Optional: Anzahl Vorlagen je Bereich als kleine Zahl in der Pille. */
    counts?: Record<TemplateOwner, number> | null
  }>()

  const emit = defineEmits<{ 'update:modelValue': [owner: TemplateOwner] }>()

  const buttons = new Map<TemplateOwner, HTMLButtonElement>()

  function setButtonRef (owner: TemplateOwner, el: Element | ComponentPublicInstance | null) {
    if (el instanceof HTMLButtonElement) buttons.set(owner, el)
    else buttons.delete(owner)
  }

  function choose (owner: TemplateOwner) {
    if (owner !== props.modelValue) emit('update:modelValue', owner)
  }

  function onKeydown (event: KeyboardEvent) {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return
    event.preventDefault()
    const values = TEMPLATE_OWNER_OPTIONS.map(o => o.value)
    const step = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1
    const next = values[(values.indexOf(props.modelValue) + step + values.length) % values.length]
    if (!next) return
    choose(next)
    buttons.get(next)?.focus()
  }
</script>

<style scoped>
.tos {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.tos__btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: var(--kars-pill-height, 32px);
  padding: 0 16px;
  border: 1px solid var(--kars-pill-border);
  border-radius: 999px;
  background: var(--kars-pill-bg);
  color: var(--kars-pill-text);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
}

.tos__btn:hover:not(.is-active) { border-color: var(--kars-pill-hover-border); }
.tos__btn:focus-visible { outline: 2px solid var(--kars-focus-ring); outline-offset: 2px; }

.tos__btn.is-active {
  background: var(--kars-pill-active);
  border-color: var(--kars-pill-active);
  color: var(--kars-pill-active-text);
}

.tos__count {
  min-width: 18px;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  background: var(--grey-light-color, #f3f5f7);
  color: var(--grey-color, #5f5f5f);
}

.tos__btn.is-active .tos__count {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}
</style>
